import type { Author } from "@marketing/shared/lib/authors";
import { cn } from "@ui/lib";
import Image from "next/image";
import type { ReactNode } from "react";

/** "September 10, 2026" from an ISO date, without a timezone shift. */
export function formatLongDate(iso: string): string {
	const [year, month, day] = iso.split("-").map(Number);
	return new Intl.DateTimeFormat("en-US", { dateStyle: "long" }).format(
		new Date(year, month - 1, day),
	);
}

/**
 * Author and freshness row for marketing content: avatar, name, a visible
 * "Last updated" date in a `<time>` element, and an optional trailing note
 * (a series count, a reading time). Pair it with `author`, `datePublished`,
 * and `dateModified` in the page's JSON-LD so the visible and structured
 * signals agree.
 */
export function Byline({
	author,
	updated,
	trailing,
	className,
}: {
	author: Author;
	/** ISO date (YYYY-MM-DD) of the last editorial pass. */
	updated: string;
	trailing?: ReactNode;
	className?: string;
}) {
	return (
		<div
			className={cn(
				"flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted-foreground",
				className,
			)}
		>
			<div className="flex items-center">
				<div className="relative mr-2 size-8 overflow-hidden rounded-full">
					<Image
						src={author.image}
						alt={author.name}
						fill
						sizes="96px"
						className="object-cover object-center"
					/>
				</div>
				<span className="font-semibold">{author.name}</span>
			</div>
			<p>
				Last updated{" "}
				<time dateTime={updated}>{formatLongDate(updated)}</time>
			</p>
			{trailing && <p>{trailing}</p>}
		</div>
	);
}
